import React, { useState } from 'react';
import {
  Modal, Upload, Button, Typography, message, Space, Divider
} from 'antd';
import {
  InboxOutlined, UploadOutlined, DownloadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import sampleExcel from '../../assets/sample-medications.xlsx';
import { MedicationAPI } from '../../MedicationAPI';

const { Title, Text } = Typography;

const BulkUploadPopup = ({ visible, onClose, onUploadSuccess }) => {
  const [fileList, setFileList] = useState([]);

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.warning("Please select at least one Excel file.");
      return;
    }

    fileList.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(sheet);

          const transformed = data.map(row => ({
            name: row.name,
            generic: row.generic,
            qty: parseInt(row.qty),
            price_per_strip: parseFloat(row.price_per_strip),
            expiry: dayjs(row.expiry).format('YYYY-MM-DD'),
          }));

          await Promise.all(transformed.map(item => MedicationAPI.create(item)));
          message.success(`${file.name} imported successfully`);

          if (index === fileList.length - 1) {
            setFileList([]);
            onUploadSuccess?.();
            onClose();
          }
        } catch (err) {
          console.error('❌ Excel import failed:', err);
          message.error(`${file.name} import failed`);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={() => {
        setFileList([]);
        onClose();
      }}
      footer={null}
      centered
      width={620}
      maskClosable={false}
      bodyStyle={{
        backgroundColor: '#fff',
        borderRadius: 16,
        padding: 28,
      }}
      maskStyle={{
        backdropFilter: 'blur(6px)',
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
      }}
      title={
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          Bulk Upload Medications
        </Title>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Format your Excel sheet like the sample below. Column headers must match exactly.
        </Text>

        <div style={{ textAlign: 'center', marginTop: 4, marginBottom: 12 }}>
  <Button
    type="link"
    icon={<DownloadOutlined />}
    href={sampleExcel}
    download="sample-medications.xlsx"
    style={{ fontWeight: 500, fontSize: 14 }}
  >
    Download Sample Excel File
  </Button>
</div>


        <Divider style={{ margin: '8px 0' }} />

        <Upload.Dragger
          multiple
          accept=".xlsx,.xls"
          fileList={fileList}
          beforeUpload={(file) => {
            setFileList(prev => [...prev, file]);
            return false;
          }}
          onRemove={(file) => {
            setFileList(prev => prev.filter(f => f.uid !== file.uid));
          }}
          style={{
            borderRadius: 8,
            backgroundColor: '#fafafa',
            padding: 12,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <InboxOutlined style={{ fontSize: 32, color: '#1677ff' }} />
            <p style={{ margin: '8px 0 4px', fontSize: 14, fontWeight: 500 }}>
              Click or drag Excel files here to upload
            </p>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Only .xls and .xlsx files are supported (max 5 files)
            </Text>
          </div>
        </Upload.Dragger>

        <Button
          type="primary"
          icon={<UploadOutlined />}
          block
          size="large"
          onClick={handleUpload}
          disabled={fileList.length === 0}
        >
          Upload File{fileList.length > 1 ? 's' : ''}
        </Button>
      </Space>
    </Modal>
  );
};

export default BulkUploadPopup;
